import React, { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { PrivateRoutes, ApplicationRoute } from "./protectRoutes";
import Navbar from "./components/common/navbar/Navbar";
import Footer from "./components/common/footer/Footer";
import DOCUMENTS from "./pages/documents";

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const Home = lazy(() => import("./pages/home/Home"));
const Datasets = lazy(() => import("./pages/datasets/Datasets"));
const Challenges = lazy(() => import("./pages/datasets/Challenges"));
const DocumentViewer = lazy(() => import("./pages/datasets/DocumentViewer"));
const Application = lazy(() => import("./pages/application/Application"));
const ApplicationList = lazy(() => import("./pages/application/ApplicationList"));
const SingleApplication = lazy(() => import("./pages/application/SingleApplication"));
const Readmd = lazy(() => import("./pages/display_markdown/RenderDoc"));
const NotFoundPage = lazy(() => import("./pages/404page"));
const ViewApplication = lazy(() => import("./pages/application/user/ViewApplication"));
const FeedbackForm = lazy(() => import("./pages/feedback"));
const Stats = lazy(() => import("./pages/stats"));
const DisplayModels = lazy(() => import("./pages/models/AllModels"));
const ExternalRedirect = lazy(() => import("./pages/Mainframe"));
const PredictionUpload = lazy(() => import("./pages/models/Screening"));
const About = lazy(() => import("./pages/home/About"));
const ModelCard = lazy(() => import("./pages/models/ModelCard"));
const ProfilePage = lazy(() => import("./pages/home/Profile"));
const MediaGallery = lazy(() => import("./pages/media/Gallery"));
const EventPublicationsPage = lazy(() => import("./pages/media/Resource"));
const MediaSubmissionForm = lazy(() => import("./pages/media/Form"));
const DataPackage = lazy(() => import("./pages/home/DataPackage"));
const ResourcesPage = lazy(() => import("./pages/home/Resource_Page"));
const GitHubContentRenderer = lazy(() => import("./components/common/git_render/Common"));
const PrivacyPolicy = lazy(() => import("./components/common/footer/PrivacyPolicy"));
const DocumentView = lazy(() => import("./pages/DocumentView"));

function BasicRoutes() {
  const location = useLocation();

  // Scroll to top synchronously before paint on every route change AND on
  // the initial mount (which is what fires on a hard refresh).
  useLayoutEffect(() => {
    if (location.hash) return;
    if (location.state?.scrollToAbout) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, location.state]);

  useEffect(() => {
    const handler = () => {
      if (window.location.hash) return;
      window.scrollTo(0, 0);
    };
    if (document.readyState === "complete") {
      handler();
    } else {
      window.addEventListener("load", handler, { once: true });
      return () => window.removeEventListener("load", handler);
    }
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {location.pathname !== "/mainframe" && <Navbar />}

      <main className="flex-1 flex flex-col">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} exact />
            <Route path="/datasets" element={<Datasets />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/viewer" element={<DocumentViewer />} />
            <Route path="/models" element={<DisplayModels />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/feedbackform" element={<FeedbackForm />} />
            <Route path="/readme" element={<Readmd />} />
            <Route path="/mainframe" element={<ExternalRedirect />} />
            <Route element={<ApplicationRoute />}>
              <Route path="/application" element={<Application />} />
            </Route>
            <Route element={<PrivateRoutes />}>
              <Route path="/application-list" element={<ApplicationList />} />
              <Route path="/single-application/:applicationId" element={<SingleApplication />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/view-application/:applicationId" element={<ViewApplication />} />
            <Route path="/screening/:runId/:targetName" element={<PredictionUpload />} />
            <Route path="/home" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/modelcard/:runId/:targetName" element={<ModelCard />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/media" element={<MediaGallery />} />
            <Route path="/resources" element={<EventPublicationsPage />} />
            <Route path="/submission" element={<MediaSubmissionForm />} />
            <Route path="/package" element={<DataPackage />} />
            <Route path="/research" element={<ResourcesPage />} />
            <Route path="/github-content" element={<GitHubContentRenderer />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            {/* Link-only PDF pages (no navbar entry) — see pages/documents.js.
                Each slug is reachable at /<slug> and /document/<slug>. */}
            {Object.keys(DOCUMENTS).map((slug) => (
              <Route
                key={slug}
                path={`/${slug}`}
                element={<DocumentView slug={slug} />}
              />
            ))}
            <Route path="/document/:slug" element={<DocumentView />} />
          </Routes>
        </Suspense>
      </main>
      {location.pathname !== "/mainframe" && <Footer />}
    </div>
  );
}

export default BasicRoutes;
